/**
 * v1.14.69.0 — Cyber Risk Wizard 9-step
 *
 * Stesso pattern DOWizard:
 *  - radio Sì/No esplicito + description condizionale
 *  - bilanci=N/A (cyber non ha bilanci)
 *  - submit summary banner errori
 *  - auto-prefill da from_do={id} se query string presente (legge dal backend)
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { Loader2, Shield, AlertCircle, Download, Lock } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import QuestionarioIntroBanner from "@/components/QuestionarioIntroBanner";
import { WizardPreExportSummary } from "@/components/WizardPreExportSummary";
import { WizardDownloadPanel } from "@/components/wizard/WizardDownloadPanel";
import { computeAssuntiviWarnings, findPlaceholders } from "@/lib/assuntivi_warnings";
import { enrichLabelWithYear } from "@/lib/exerciseYear";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import { IvassConsentsStepUnified, registerConsentsBulk } from "@/components/wizard/IvassConsentsStepUnified";
import ConsensiGranulari, { buildConsensiGranulariMeta, isConsensiOk } from "@/components/ConsensiGranulari";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";
import AcceptAllInformative from "@/components/AcceptAllInformative";
import { ArrayField, MultiPercentageField } from "@/components/wizard/ArrayField";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "cyber_draft_v1";

/* ---------- shared field components (small inline duplication of DOWizard) ---------- */

function YesNoRadio({ tid, value, onChange }) {
  const isYes = value === true;
  const isNo = value === false;
  const base = "px-4 py-2 rounded-full text-sm font-medium transition-colors border";
  return (
    <div className="flex gap-2" data-testid={`${tid}-radio-group`}>
      <button type="button" data-testid={`${tid}-yes`}
              onClick={() => onChange(true)} aria-pressed={isYes}
              className={`${base} ${isYes
                ? "bg-[#163321] text-white border-[#163321]"
                : "bg-white text-[#163321] border-[#163321]/40 hover:bg-[#163321]/5"}`}>
        Sì
      </button>
      <button type="button" data-testid={`${tid}-no`}
              onClick={() => onChange(false)} aria-pressed={isNo}
              className={`${base} ${isNo
                ? "bg-[#7A1D1D] text-white border-[#7A1D1D]"
                : "bg-white text-[#7A1D1D] border-[#7A1D1D]/40 hover:bg-[#7A1D1D]/5"}`}>
        No
      </button>
    </div>
  );
}

function FieldInline({ sezKey, campoKey, campo, value, onChange,
                       readOnly = false }) {
  const tid = `cyber-field-${sezKey}-${campoKey}`;
  const v = value ?? "";
  if (campo.type === "boolean") {
    return <YesNoRadio tid={tid} value={value} onChange={onChange} />;
  }
  if (campo.type === "array" && campo.item_schema) {
    return <ArrayField label={null} value={Array.isArray(value) ? value : []}
                          itemSchema={campo.item_schema}
                          maxItems={campo.max_items || 5}
                          minItems={campo.min_items || 0}
                          onChange={onChange} testId={tid} />;
  }
  if (campo.type === "multi_percentage" && Array.isArray(campo.slots)) {
    return <MultiPercentageField label={null} slots={campo.slots}
                                     value={typeof value === "object" && value ? value : {}}
                                     onChange={onChange} testId={tid} />;
  }
  if (campo.type === "textarea") {
    return <Textarea data-testid={tid} value={v} rows={3} readOnly={readOnly}
                      onChange={(e) => onChange(e.target.value)}
                      className="rounded-xl border-[#E5E2DB] max-w-full" />;
  }
  if (campo.type === "select" && Array.isArray(campo.options)) {
    return (
      <Select value={v || ""} onValueChange={onChange} disabled={readOnly}>
        <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid={tid}>
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          {campo.options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  const inputType = campo.type === "date" ? "date"
                  : campo.type === "email" ? "email"
                  : campo.type === "numeric" || campo.type === "currency" ? "number"
                  : "text";
  return (
    <div className="relative">
      {campo.type === "currency" && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#4A5E51] pointer-events-none">€</span>
      )}
      <Input type={inputType} data-testid={tid} value={v}
              readOnly={readOnly}
              placeholder={campo.placeholder || ""}
              onChange={(e) => onChange(e.target.value)}
              className={`rounded-xl border-[#E5E2DB] h-11 ${campo.type === "currency" ? "pl-7" : ""} max-w-full`} />
    </div>
  );
}

function CampoRow({ sezKey, campoKey, campo, value, formData, sectionData, sezCampi,
                    onChange, setField, readOnly = false }) {
  const tid = `cyber-field-${sezKey}-${campoKey}`;
  const isYes = value === true;

  // FASE 7.7 — resolve description child
  const parts = campo.description_field
    ? String(campo.description_field).split(".") : null;
  const descSezKey = parts && parts.length === 2 ? parts[0] : sezKey;
  const descCampoKey = parts
    ? (parts.length === 2 ? parts[1] : parts[0]) : null;
  const descChildCampo = descCampoKey && descSezKey === sezKey
    ? (sezCampi || {})[descCampoKey] : null;
  const skipInlineDesc = !!(descChildCampo && descChildCampo.required);

  // FASE 7.7 — cleanup children quando l'utente passa Yes → No/null
  const handleChange = (newVal) => {
    if (value === true && newVal !== true) {
      if (campo.requires_description_if_yes && descCampoKey && !skipInlineDesc) {
        setField(descSezKey, descCampoKey, undefined);
      }
      if (sezCampi) {
        Object.entries(sezCampi).forEach(([k, c]) => {
          if (c && c.conditional_on === campoKey) {
            setField(sezKey, k, undefined);
          }
        });
      }
    }
    onChange(newVal);
  };

  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5E2DB] hover:border-[#163321]/30 transition-colors">
      <div className="flex flex-col gap-2">
        <Label className="block text-sm text-[#0F1F14] font-medium leading-relaxed whitespace-pre-wrap break-words">
          {enrichLabelWithYear(campo.label)}
          {campo.required && <span className="text-red-600 ml-1">*</span>}
          {campo.help_text && (
            <span className="relative inline-block ml-1.5 group align-middle">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#D9C49A] text-[#163321] text-[10px] font-bold cursor-help"
                    data-testid={`cyber-help-${sezKey}-${campoKey}`}>?</span>
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-20 w-64 text-xs bg-[#163321] text-white p-2 rounded-lg shadow-lg leading-snug normal-case font-normal pointer-events-none">
                {campo.help_text}
              </span>
            </span>
          )}
          {campo.pdf_ref && (
            <span className="inline-block ml-2 text-[10px] text-[#7A8E83] bg-[#F5F4F0] rounded px-1.5 py-0.5">
              {campo.pdf_ref}
            </span>
          )}
        </Label>
        {campo.description && (
          <p className="text-xs text-gray-600 italic leading-relaxed">
            {campo.description}
          </p>
        )}
        <FieldInline sezKey={sezKey} campoKey={campoKey} campo={campo}
                      value={value} onChange={handleChange} readOnly={readOnly} />
        {isYes && campo.requires_description_if_yes && campo.description_field
          && !skipInlineDesc && (() => {
          const p2 = String(campo.description_field).split(".");
          const tgtSez = p2.length === 2 ? p2[0] : sezKey;
          const tgtKey = p2.length === 2 ? p2[1] : p2[0];
          const tgtVal = (formData[tgtSez] || {})[tgtKey];
          return (
            <div className="mt-2 pl-3 border-l-2 border-[#163321]/30">
              <Label className="block text-sm text-[#0F1F14] mb-1.5">
                {campo.description_if_yes_label}
                <span className="text-red-600 ml-1">*</span>
              </Label>
              <Textarea rows={3} value={tgtVal || ""}
                         data-testid={`${tid}-desc`}
                         onChange={(e) => setField(tgtSez, tgtKey, e.target.value)}
                         className="rounded-xl border-[#E5E2DB] max-w-full" />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function SectionBlock({ sezKey, sez, formData, setField, anagraficaReadOnly }) {
  const sectionData = formData[sezKey] || {};
  // FASE 7.7 — hide description fields managed by a parent boolean.
  // ECCEZIONE: se il child ha `required: true` a schema, resta visibile standalone.
  const hiddenByParent = new Set();
  Object.values(sez.campi).forEach((c) => {
    if (c.requires_description_if_yes && c.description_field) {
      const p = String(c.description_field).split(".");
      const lk = p.length === 2 && p[0] === sezKey ? p[1] : p[0];
      if (!lk) return;
      const childCampo = sez.campi[lk];
      if (childCampo && childCampo.required) return; // required standalone → non nascondere
      hiddenByParent.add(lk);
    }
  });
  const readOnly = sezKey === "anagrafica" && anagraficaReadOnly;
  return (
    <div className="space-y-4" data-testid={`cyber-section-${sezKey}`}>
      <div>
        <h2 className="font-heading text-xl text-[#0F1F14] font-medium leading-snug">
          {sez.label}
        </h2>
        {sez.subtitle && <p className="text-sm text-[#4A5E51]">{sez.subtitle}</p>}
        {readOnly && (
          <p className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 mt-2 flex items-center gap-2"
              data-testid="cyber-anagrafica-readonly-notice">
            <Lock className="w-3 h-3" />
            Dati anagrafici pre-popolati dal Questionario D&amp;O collegato — non modificabili.
          </p>
        )}
      </div>
      <div className="space-y-3">
        {Object.entries(sez.campi).map(([campoKey, campo]) => {
          if (hiddenByParent.has(campoKey)) return null;
          if (campo.conditional_on && sectionData[campo.conditional_on] !== true) return null;
          return (
            <CampoRow key={campoKey}
                       sezKey={sezKey} campoKey={campoKey} campo={campo}
                       value={sectionData[campoKey]}
                       formData={formData} sectionData={sectionData}
                       sezCampi={sez.campi}
                       onChange={(v) => setField(sezKey, campoKey, v)}
                       setField={setField}
                       readOnly={readOnly} />
          );
        })}
      </div>
    </div>
  );
}

/* ---------- main wizard ---------- */
export default function CyberWizard() {
  const [searchParams] = useSearchParams();
  const fromDoId = searchParams.get("from_do");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [contact, setContact] = useState({
    client_name: "", client_email: "", client_telefono: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitErrors, setSubmitErrors] = useState(null);
  const [showPreExport, setShowPreExport] = useState(false);
  const [anagraficaPrefilled, setAnagraficaPrefilled] = useState(false);
  // FASE 13 — IVASS consents unificato: value cumulativo (accepted + docs list)
  const [ivassBulk, setIvassBulk] = useState({ accepted: false, document_ids: [] });
  const ivassMandatoryOk = !!ivassBulk.accepted;
  // FASE 8.4 click-wrap declaration
  const [signedDeclaration, setSignedDeclaration] = useState(false);
  // FIX 6 (v1.54.0.0) — MUP visione checkbox (audit compliance IVASS 40/2018)
  const [mupSeen, setMupSeen] = useState(false);
  const [mupFilename, setMupFilename] = useState(null);
  // v1.66 (Fase 2 Privacy) — Consensi granulari GDPR
  const [consensi, setConsensi] = useState({});
  // v1.72.3 — Cross-wizard: preferenze ricontatto + accept-all informative
  const [preferenzeRicontatto, setPreferenzeRicontatto] = useState({ canale: "", fascia: "" });
  const handleAcceptAllInformative = () => {
    setSignedDeclaration(true);
    setMupSeen(true);
    setConsensi((prev) => ({ ...prev, marketing: true, profilazione: true, gruppo_partner: true }));
  };
  const sessionHash = useMemo(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID() : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  []);

  // Load schema + prefill from D&O + localStorage draft (ordine deterministico)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/cyber/form-fields`);
        setSchema(data);

        // 1) PRIMA prefill da D&O linkato (se presente)
        let prefilledAnag = null;
        if (fromDoId) {
          try {
            const { data: doData } = await axios.get(
              `${API}/do/pratiche-public-snapshot/${fromDoId}`
            );
            if (doData && (doData.ragione_sociale || doData.partita_iva)) {
              prefilledAnag = {
                ragione_sociale: doData.ragione_sociale || "",
                legale_rappresentante: doData.legale_rappresentante || "",
                piva_cf: doData.partita_iva || doData.codice_fiscale || "",
                sede_legale: [doData.indirizzo_sede_legale,
                              doData.citta, doData.cap]
                              .filter(Boolean).join(", "),
                codice_ateco: doData.codice_ateco || "",
                indirizzo: doData.indirizzo_sede_legale || "",
                citta: doData.citta || "",
                provincia: doData.provincia || "",
                cap: doData.cap || "",
                email: doData.email_legale_rappresentante || "",
                telefono_cellulare: doData.telefono_legale_rappresentante || "",
              };
              setContact((prev) => ({
                ...prev,
                client_name: prev.client_name
                              || doData.legale_rappresentante || "",
                client_email: prev.client_email
                              || doData.email_legale_rappresentante || "",
                client_telefono: prev.client_telefono
                                  || doData.telefono_legale_rappresentante || "",
              }));
              setAnagraficaPrefilled(true);
            } else {
              console.warn("[Cyber] snapshot returned empty data", doData);
            }
          } catch (e) {
            console.error("[Cyber] snapshot fetch error:", e?.response?.status, e?.message);
          }
        }

        // 2) POI localStorage restore (rispettando l'anagrafica già prefilled)
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const p = JSON.parse(raw);
            if (p.formData) {
              if (prefilledAnag) {
                // mantieni anagrafica prefilled, ma usa il resto del draft
                setFormData({ ...p.formData, anagrafica: prefilledAnag });
              } else {
                setFormData(p.formData);
              }
            } else if (prefilledAnag) {
              setFormData({ anagrafica: prefilledAnag });
            }
            if (p.contact && !fromDoId) setContact(p.contact);
            if (p.currentStep) setCurrentStep(p.currentStep);
          } else if (prefilledAnag) {
            setFormData({ anagrafica: prefilledAnag });
          }
        } catch (e) { /* ignore */ }
      } catch (e) {
        toast.error("Errore caricamento schema Cyber");
      } finally {
        setLoading(false);
      }
    })();
  }, [fromDoId]);

  // localStorage autosave
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(LS_KEY,
          JSON.stringify({ formData, contact, currentStep }));
      } catch (e) { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [formData, contact, currentStep]);

  const setField = useCallback((sez, key, value) => {
    setFormData((prev) => ({
      ...prev, [sez]: { ...(prev[sez] || {}), [key]: value },
    }));
  }, []);

  const orderedSteps = useMemo(() => {
    if (!schema?.wizard?.steps) return [];
    const steps = schema.wizard.steps.map((s) => ({
      ...s, sez: { ...schema.sezioni[s.key], key: s.key },
    }));
    // FASE 8.2 — inject "compliance" step just before the final review (last step)
    // it becomes the second-to-last step, so the wizard now has (schema_steps + 1) total
    steps.push({ key: "__ivass_compliance__", title: "Documenti IVASS",
                   sez: { key: "__ivass_compliance__", campi: {} }, virtual: true });
    return steps;
  }, [schema]);

  // FASE 4.5 — apri pre-export invece di submit diretto (chiamato dal FormNavigation)
  const openPreExport = () => {
    setSubmitErrors(null);
    setShowPreExport(true);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
  };

  const handleSubmit = async () => {
    setShowPreExport(false);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/cyber/questionario`, {
        form_data: formData,
        client_name: contact.client_name,
        client_email: contact.client_email,
        client_telefono: contact.client_telefono,
        from_do_id: fromDoId,
        schema_version: "cyber_v1_full",
        signed_declaration: signedDeclaration,
        session_hash: sessionHash,
        mup_visione: buildMupVisioneMeta({ mupSeen, mupFilename }),
        consensi_granulari: buildConsensiGranulariMeta(consensi),
        // v1.72.3 — Preferenze ricontatto (canale + fascia oraria)
        preferenze_ricontatto: (preferenzeRicontatto.canale || preferenzeRicontatto.fascia)
          ? preferenzeRicontatto : null,
      });
      // FASE 13 — registra i consensi IVASS in modalità bulk cumulativa
      const submissionId = data.id || data.id_pratica;
      if (submissionId && ivassBulk.accepted && ivassBulk.document_ids.length) {
        try {
          await registerConsentsBulk({
            submissionId,
            documentIds: ivassBulk.document_ids,
            module: "cyber",
          });
        } catch (err) { /* audit non blocca */ }
      }
      setSubmitResult(data);
      setSubmitErrors(null);
      try { localStorage.removeItem(LS_KEY); } catch (e) { /* ignore */ }
      toast.success("Cyber Risk inviato!");
    } catch (e) {
      const det = e.response?.data?.detail;
      const errs = det?.errors || [];
      if (errs.length) {
        setSubmitErrors(errs);
        toast.error(`${errs.length} campi da correggere`);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (er) { /* ignore */ }
      } else {
        toast.error(det?.message || det || "Errore invio");
      }
    } finally { setSubmitting(false); }
  };

  if (loading || !schema) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#163321]" />
        </main>
        <Footer />
      </>
    );
  }

  if (submitResult) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="cyber-thanks">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
            <WizardDownloadPanel
              title="Preventivi Cyber generati!"
              downloads={submitResult.downloads || []}
              submissionId={submitResult.id}
              ramoLabel="Cyber Risk"
              clientName={contact.client_name} />
            {submitResult.from_do_id && (
              <p className="mt-4 text-xs bg-[#D9C49A]/25 text-[#163321] rounded-lg px-3 py-2 inline-block">
                Pratica collegata al Questionario D&amp;O #{submitResult.from_do_id.slice(0, 8)}
              </p>
            )}
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalSteps = orderedSteps.length;
  const stepIdx = Math.max(0, currentStep - 1);
  const step = orderedSteps[stepIdx];
  const progress = ((stepIdx + 1) / totalSteps) * 100;

  return (
    <>
      <Header />
      <Toaster richColors position="top-center" />
      <main className="min-h-screen bg-[#FCFCFA] pt-20 overflow-x-hidden"
            data-testid="cyber-wizard-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-2"
                data-testid="cyber-wizard-title">
              Questionario Cyber Risk
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
              AIG Advisors · Polizza Cyber Insurance · 22 domande + sinistri
            </p>
            {fromDoId && (
              <p className="mt-3 inline-block text-xs bg-[#D9C49A]/25 text-[#163321] rounded-lg px-3 py-1.5"
                  data-testid="cyber-linked-banner">
                Collegato a Pratica D&amp;O #{fromDoId.slice(0, 8)}
              </p>
            )}
          </div>

          <QuestionarioIntroBanner ramoRischio="Cyber" />

          {/* FASE 4.5 — Pre-export summary */}
          {showPreExport && (
            <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6"
                  data-testid="cyber-wizard-pre-export-container">
              <WizardPreExportSummary
                formData={formData}
                validationErrors={[]}
                warnings={computeAssuntiviWarnings(formData)}
                placeholders={findPlaceholders(formData)}
                onProceed={handleSubmit}
                onBack={() => setShowPreExport(false)}
                submitting={submitting}
                canProceed={isIvassComplianceOk({ signedDeclaration, mupSeen }) && isConsensiOk(consensi)}
              />
              {/* v1.72.3 — Preferenze ricontatto (canale + fascia) */}
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-3">Come preferisci essere ricontattato/a?</p>
                <ContattoPreferenzeBlock
                  value={preferenzeRicontatto}
                  onChange={setPreferenzeRicontatto}
                  testidPrefix="cyber-ricontatto"
                />
              </div>
              {/* v1.72.3 — Accetta tutte le informative facoltative */}
              <div className="mt-5">
                <AcceptAllInformative
                  onAcceptAll={handleAcceptAllInformative}
                  testid="cyber-accept-all-informative"
                />
              </div>
              {/* FASE 8.4 + FIX 6 — Click-wrap dichiarazioni IVASS (veridicità + MUP) */}
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]"
                    data-testid="cyber-signature-declaration">
                <IVASSComplianceCheckboxes
                  signedDeclaration={signedDeclaration}
                  onSignedDeclarationChange={setSignedDeclaration}
                  mupSeen={mupSeen}
                  onMupSeenChange={setMupSeen}
                  mupFilename={mupFilename}
                  onMupFilenameChange={setMupFilename}
                  testIdPrefix="cyber"
                />
              </div>
              {/* v1.66 — Consensi granulari GDPR */}
              <div className="mt-4">
                <ConsensiGranulari
                  value={consensi}
                  onChange={setConsensi}
                  testIdPrefix="cyber"
                  includeSanitario={false}
                />
              </div>
            </div>
          )}

          {!showPreExport && submitErrors && submitErrors.length > 0 && (
            <div className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5"
                  data-testid="cyber-submit-errors-banner"
                  role="alert"
                  aria-live="polite">
              <h3 className="font-heading text-base text-red-800 font-medium mb-1 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                {submitErrors.length === 1
                  ? "1 campo da correggere prima di inviare"
                  : `${submitErrors.length} campi da correggere prima di inviare`}
              </h3>
              <p className="text-xs text-red-600/80 mb-3 ml-7">
                Rivedi i seguenti campi e completa l&apos;invio.
              </p>
              <ul className="space-y-1.5 mt-3">
                {submitErrors.map((e, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2"
                       data-testid={`cyber-submit-error-${i}`}>
                    <span className="text-red-400 mt-0.5" aria-hidden="true">•</span>
                    <span className="flex-1">{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <StepProgress
            steps={orderedSteps.map((s) => s.title)}
            currentStep={stepIdx}
            onStepClick={(i) => { setShowPreExport(false); setCurrentStep(i + 1); }}
            progress={progress}
            testIdPrefix="cyber"
          />

          <div className={showPreExport ? "hidden" : "bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6 max-w-full overflow-hidden"}>
            {stepIdx === 0 && (
              <div className="bg-[#FDF8E8] border border-[#D9C49A] rounded-2xl p-5 mb-5">
                <h3 className="font-heading text-base text-[#0F1F14] font-medium mb-3">
                  I tuoi contatti
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[["client_name", "Nome e cognome *", "text"],
                    ["client_email", "Email *", "email"],
                    ["client_telefono", "Telefono", "tel"]].map(([k, l, t]) => (
                    <div key={k}>
                      <Label className="text-sm text-[#0F1F14] block mb-1.5">{l}</Label>
                      <Input type={t} data-testid={`cyber-contact-${k}`}
                              value={contact[k]}
                              onChange={(e) => setContact({ ...contact, [k]: e.target.value })}
                              className="rounded-xl border-[#E5E2DB] h-11 max-w-full" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step && step.key === "__ivass_compliance__" && (
              <IvassConsentsStepUnified
                module="cyber"
                groupFilter={["colucci_trasversali", "compagnia_aig", "compagnia_reale", "compagnia_lloyds"]}
                value={ivassBulk}
                onChange={setIvassBulk}
              />
            )}

            {step && step.key !== "__ivass_compliance__" && (
              <SectionBlock sezKey={step.key} sez={step.sez}
                             formData={formData} setField={setField}
                             anagraficaReadOnly={anagraficaPrefilled} />
            )}
          </div>

          {!showPreExport && (
            <FormNavigation
              step={stepIdx} totalSteps={totalSteps}
              onPrev={() => setCurrentStep(Math.max(1, currentStep - 1))}
              onNext={() => setCurrentStep(Math.min(totalSteps, currentStep + 1))}
              onSubmit={openPreExport}
              submitting={submitting}
              canSubmit={ivassMandatoryOk}
              testIdPrefix="cyber"
            />
          )}
          {!showPreExport && !ivassMandatoryOk && stepIdx === totalSteps - 1 && (
            <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                data-testid="cyber-ivass-mandatory-warning">
              Devi accettare tutti i documenti obbligatori prima di poter inviare la proposta.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
